<template>
  <div class="relative py-4">
    <vs-row class="my-4 header-flex">
      <div class="title-section">
        <div v-if="!!title && titleType === 'small'" style="font-weight: 500; font-size: 14px">
          {{ title }}
          <span v-if="subtitle" class="subtitle-pc">{{ subtitle }}</span>
        </div>
        <div v-else-if="!!title && titleType === 'html'" class="items-center">
          <h5 v-html="title"></h5>
          <span v-if="subtitle" class="subtitle-pc" v-html="subtitle"></span>
        </div>
        <div v-else-if="!!title && titleType === 'circleHtml'" class="flex items-center">
          <i class="dot"/>
          <h5 v-html="title"></h5>
          <span v-if="subtitle" class="subtitle-pc" v-html="subtitle"></span>
        </div>
        <div v-else-if="!!title && titleType === 'tooltip'" class="flex">
          <h5 v-html="title" />
          <span v-if="subtitle" class="subtitle-pc" v-html="subtitle"></span>
          <vx-tooltip :title="tooltipHead" :html="tooltipBody" style="margin-left: 4px; display: flex; align-items: center; justify-content: center" position="top" delay=".1s">
            <feather-icon icon="AlertCircleIcon" style="cursor: pointer; width: 16px; color: #7e72f2" @click="onClickToolTip()" />
          </vx-tooltip>
        </div>
        <h5 v-else-if="!!title" class="flex items-center">
          <i class="dot" v-if="titleType !== 'noCircle'"></i>
          {{ title }}
          <span v-if="subtitle" class="subtitle-pc">{{ subtitle }}</span>
        </h5>
        <div v-else></div>
      </div>
      <div v-if="controls && !board.readonly && !isPrint" class="header-buttons">
        <slot name="headButton"/>
        <!-- Desktop: 항목추가 + 선택삭제 -->
        <vs-button
          class="desktop-only"
          color="secondary"
          type="border"
          @click="handleAddRow"
        >
          항목추가
        </vs-button>
        <!-- PC/Mobile 공통: 선택삭제 -->
        <vs-button
          class="ml-4"
          color="secondary"
          @click="handleRemoveRowConfirm"
          :disabled="selectedItemsCount === 0"
        >
          선택삭제
        </vs-button>
      </div>
    </vs-row>

    <!-- Mobile subtitle (title과 선택삭제 버튼 아래) -->
    <div v-if="subtitle && !isPrint" class="mobile-subtitle">
      <span v-html="subtitle"></span>
    </div>

    <!-- 빈 목록 안내 메시지 -->
    <div v-if="list.length === 0 && !isPrint" class="empty-list-message">
      <div class="empty-content">
        <i class="feather icon-inbox empty-icon"></i>
        <p class="empty-text">항목을 추가해주세요</p>
        <vs-button
          v-if="controls && !board.readonly"
          color="primary"
          type="filled"
          @click="handleAddRow"
          class="empty-add-btn"
        >
          <i class="feather icon-plus mr-2"></i>
          첫 번째 항목 추가
        </vs-button>
      </div>
    </div>

    <!-- Desktop 테이블 레이아웃 -->
    <div class="desktop-layout">
      <table :class="[thema === 'label' && 'label-table']">
        <colgroup>
          <col v-if="multiple && !isPrint && !board.readonly" width="60" />
          <template v-for="(col, col_i) in tableData">
            <template v-if="col.type === 'checks'">
              <col v-for="checkTitle of col.selectData" :key="`col_${col_i}_${checkTitle}`" :width="!!col.width && col.width" class="text-center" />
            </template>
            <template v-else>
              <col :key="`col_${col_i}_${col.id}`" :width="!!col.width && col.width" />
            </template>
          </template>
        </colgroup>
      <thead>
        <tr ref="doubleHead">
          <slot name="doubleHead"></slot>
        </tr>

        <tr>
          <th v-if="multiple && !isPrint && !board.readonly">
            <vs-checkbox :value="checkData.length === list.length && 0 < list.length" class="justify-center mx-0" @click.native="onClickAllCheckbox" />
          </th>
          <template v-for="th in tableData">
            <template v-if="th.type === 'checks'">
              <th v-for="checkTitle of th.selectData" :key="`${th.id}_${checkTitle}`" class="text-center">{{ checkTitle }}</th>
            </template>
            <template v-else>
              <th :key="th.id">
                <div class="w-full text-center" v-html="th.title"></div>
              </th>
            </template>
          </template>
        </tr>
      </thead>
      <tbody v-for="(tr, indextr) in list" :key="indextr">
        <tr :style="{ 'z-index': list.length - indextr }" style="box-shadow: none !important; transform: translateY(0px)" class="print-container relative">
          <td v-if="multiple && !isPrint && !board.readonly">
            <vs-checkbox v-model="checkData" :vs-value="tr.id" class="justify-center mx-0" />
          </td>
          <template v-for="cellData in tableData">
            <template v-if="cellData.type === 'checks'">
              <td  v-for="(check, checkIdx) of cellData.selectData" :key="`${check}_${checkIdx}`" class="text-center" :style="{width: cellData.width ? `${cellData.width}px` : undefined }">
                <vs-checkbox :value="tr[cellData.id] === check" :vs-value="check" @input="onClickCheckbox($event, tr, cellData.id)" class="justify-center mx-0" />
              </td>
            </template>
            <td
              v-else
              :key="cellData.id"
              class="text-center"
              :class="getItemBodyClass(cellData)">
              <div v-if="cellData.type === 'no'" :style="{ textAlign: cellData.align }">
                {{ indextr + 1 }}
              </div>
              <div v-else-if="cellData.type === 'label'" :style="{ textAlign: cellData.align }">
                {{ tr[cellData.id] }}
              </div>
              <div v-else @click.stop>
                <!-- Print -->
                <span v-if="isPrint && cellData.type === 'textarea'" class="print-textarea">
                  {{ tr[cellData.id] }}
                </span>
                <span v-else-if="isPrint && cellData.type === 'dateArr'"> {{ tr[cellData.id].sDate }} ~ {{ tr[cellData.id].eDate }} </span>
                <div v-else-if="isPrint && cellData.type === 'customType'">
                  <slot name="customType" :index="indextr" :cellData="cellData" :trData="tr" :value="tr[cellData.id]" :input="(data) => customTypeInput(indextr, cellData.id, data)"></slot>
                </div>
                <div v-else-if="isPrint">
                  <div v-if="cellData.type !== 'sign'" class="print-input">
                    {{ tr[cellData.id] }}
                  </div>
                  <div v-else style="text-align: -webkit-center">
                    <img v-if="tr[cellData.id]" :src="tr[cellData.id]" alt="Signature" class="signatureImg" />
                    <div v-else style="width: 100%; height: 80px; min-width: 80px; max-width: 120px" class="print-input"></div>
                  </div>
                </div>
                <!-- Content -->
                <div v-else>
                  <div v-if="cellData.type === 'string'" :style="{ textAlign: cellData.align }" v-html="tr[cellData.id]"></div>
                  <ml-input v-else-if="cellData.type === 'text'" :readonly="board.readonly || cellData.readonly" v-model="tr[cellData.id]" :placeholder="cellData.placeholder" :style="cellData.style? cellData.style : {}" class="w-full flex-1 text-left" @click.stop />
                  <ml-input v-else-if="cellData.type === 'valueText'" :readonly="board.readonly || cellData.readonly" :value="cellData.value" :placeholder="cellData.placeholder" class="w-full flex-1" @click.stop />
                  <slot v-else-if="cellData.type === 'calcul'" name="calcul" v-bind:data="tr" v-bind:data_i="indextr"></slot>
                  <ml-input v-else-if="cellData.type === 'fixed-text'" :readonly="true" v-model="tr[cellData.id]" :placeholder="cellData.placeholder" class="w-full flex-1" @click.stop />
                  <number-input v-else-if="cellData.type === 'number'" :readonly="board.readonly" v-model="tr[cellData.id]" :placeholder="cellData.placeholder" class="w-full flex-1" @click.stop />
                  <ml-textarea
                    v-if="cellData.type === 'textarea'"
                    height="90px"
                    :readonly="board.readonly"
                    v-model="tr[cellData.id]"
                    :placeholder="typeof cellData.placeholder === 'string' ? cellData.placeholder : cellData.placeholder[indextr]"
                    class="w-full flex-1 resize-none"
                    :style="cellData.style? cellData.style : {}"
                    @click.stop
                  />
                  <ml-input v-else-if="cellData.type === 'select' && board.readonly" v-model="tr[cellData.id]" readonly class="w-full flex-1" />
                  <ml-input v-else-if="cellData.type === 'select' && tr[cellData.id] === '직접입력'" @blur="(e, data) => customSelectBlur(indextr, cellData.id, data)" class="w-full flex-1" :renderFocus="true" />
                  <vs-select v-else-if="cellData.type === 'select'" v-model="tr[cellData.id]" :placeholder="cellData.placeholder" class="w-full flex-1" autocomplete>
                    <vs-select-item v-for="selectData in cellData.selectData" :key="selectData" :text="selectData" :value="selectData" />
                  </vs-select>
                  <date-selector v-else-if="cellData.type === 'date'" class="w-full" v-model="tr[cellData.id]" :readonly="board.readonly" @click.stop />
                  <time-selector v-else-if="cellData.type === 'time'" class="w-full" v-model="tr[cellData.id]" :readonly="board.readonly" @click.stop />
                  <period-picker v-else-if="cellData.type === 'period'" v-model="tr[cellData.id]" :readonly="board.readonly" />
                  <CompanySelector v-else-if="cellData.type === 'company'" v-model="tr[cellData.id]" :readonly="board.readonly" @click.stop />
                  <field-selector v-else-if="cellData.type === 'field'" v-model="tr[cellData.id]" :readonly="board.readonly" />
                  <div v-else-if="cellData.type === 'dateArr' && !!tr[cellData.id]" class="flex flex-col">
                    <date-selector v-model="tr[cellData.id].sDate" placeholder="시작일" :readonly="board.readonly"></date-selector>
                    <span class="px-3">~</span>
                    <date-selector v-model="tr[cellData.id].eDate" placeholder="종료일" :readonly="board.readonly"></date-selector>
                  </div>
                  <sign-field v-else-if="cellData.type === 'sign'" v-model="tr[cellData.id]" :readonly="board.readonly" />
                  <div v-else-if="cellData.type === 'radio'" class="flex justify-center items-center gap-3" :style="cellData.style? cellData.style : {}">
                    <vs-radio v-for="(radioItem, radioItem_i) in cellData.radioItems" :key="`${indextr}_${cellData.id}_${radioItem_i}`" v-model="tr[cellData.id]" :vs-name="`${indextr}_${cellData.id}`" :vs-value="radioItem.value">
                      {{ radioItem.label || radioItem.value }}
                    </vs-radio>
                  </div>
                  <div v-else-if="cellData.type === 'check'" class="flex justify-center items-center">
                    <div>
                      <vs-checkbox :value="tr[cellData.id] === cellData.title" @input="tr[cellData.id] = tr[cellData.id] === cellData.title ? '' : cellData.title" />
                    </div>
                  </div>
                  <div v-else-if="cellData.type === 'customType'">
                    <slot name="customType" :index="indextr" :cellData="cellData" :trData="tr" :value="tr[cellData.id]" :input="(data) => customTypeInput(indextr, cellData.id, data)"></slot>
                  </div>
                </div>
              </div>
            </td>
          </template>
        </tr>
      </tbody>
      <tbody>
        <slot name="customTr" :isMobile="isMobile"></slot>
      </tbody>
    </table>
    </div>

    <!-- Mobile 카드 레이아웃 -->
    <div class="mobile-layout">
      <!-- 모바일 전체 선택 헤더 (아이템이 있을 때만) -->
      <div
        class="mobile-header"
        v-if="!isPrint && list.length > 0 && multiple && !board.readonly"
        @click="toggleAllCheck"
      >
        <vs-checkbox :value="checkData.length === list.length && list.length > 0" />
        <span class="mobile-header-text">전체 선택</span>
      </div>

      <!-- 모바일 카드형 아이템들 -->
      <div class="mobile-cards-container">
        <div
          v-for="(tr, indextr) in list"
          :key="indextr"
          class="mobile-card"
          :class="{ 'selected': checkData.includes(tr.id), 'print-mode': isPrint }"
        >
          <!-- 카드 헤더 (체크박스 + 인덱스 + 삭제 버튼) -->
          <div class="mobile-card-header" v-if="!isPrint">
            <div class="mobile-card-header-left">
              <vs-checkbox
                v-if="multiple && !board.readonly"
                v-model="checkData"
                :vs-value="tr.id"
              ></vs-checkbox>
              <span class="card-index">{{ indextr + 1 }}번째 항목</span>
            </div>
            <vs-button
              v-if="controls && !board.readonly"
              class="mobile-delete-btn"
              color="danger"
              type="flat"
              size="small"
              @click="handleDeleteSingleItem(indextr)"
            >
              삭제
            </vs-button>
          </div>
          <div class="mobile-card-header print-header" v-else>
            <span class="card-index">{{ indextr + 1 }}번째 항목</span>
          </div>

          <!-- 카드 내용 -->
          <div class="mobile-card-content">
            <div v-for="cellData in tableData" :key="cellData.id" v-show="cellData.type !== 'no' && cellData.type !== 'label'">
              <div class="mobile-field" :class="{ 'radio-field': cellData.type === 'radio', 'custom-type-field': cellData.type === 'customType' }">
                <label class="mobile-field-label" v-html="cellData.title"></label>
                <div class="mobile-field-input">
                  <!-- checks 타입 처리 -->
                  <div v-if="cellData.type === 'checks'" class="mobile-checks-container">
                    <div v-for="(check, checkIdx) of cellData.selectData" :key="`${check}_${checkIdx}`" class="mobile-check-item">
                      <vs-checkbox :value="tr[cellData.id] === check" :vs-value="check" @input="onClickCheckbox($event, tr, cellData.id)" />
                      <span class="check-label">{{ check }}</span>
                    </div>
                  </div>
                  <!-- 일반 필드 처리 -->
                  <template v-else>
                    <!-- Print -->
                    <span v-if="isPrint && cellData.type === 'textarea'" class="mobile-print-value mobile-print-textarea">
                      {{ tr[cellData.id] }}
                    </span>
                    <span v-else-if="isPrint && cellData.type === 'dateArr'" class="mobile-print-value">
                      {{ tr[cellData.id].sDate }} ~ {{ tr[cellData.id].eDate }}
                    </span>
                    <div v-else-if="isPrint && cellData.type === 'customType'">
                      <slot name="customType" :index="indextr" :cellData="cellData" :trData="tr" :value="tr[cellData.id]" :input="(data) => customTypeInput(indextr, cellData.id, data)"></slot>
                    </div>
                    <div v-else-if="isPrint && cellData.type === 'sign'" class="mobile-print-sign">
                      <img v-if="tr[cellData.id]" :src="tr[cellData.id]" alt="Signature" class="mobile-signature-img" />
                      <div v-else class="mobile-print-value">서명 없음</div>
                    </div>
                    <span v-else-if="isPrint" class="mobile-print-value">
                      {{ tr[cellData.id] }}
                    </span>
                    <!-- Content -->
                    <template v-else>
                      <div v-if="cellData.type === 'string'" class="mobile-print-value" v-html="tr[cellData.id]"></div>
                      <ml-input v-else-if="cellData.type === 'text'" :readonly="board.readonly || cellData.readonly" v-model="tr[cellData.id]" :placeholder="cellData.placeholder" class="w-full" />
                      <ml-input v-else-if="cellData.type === 'valueText'" :readonly="board.readonly || cellData.readonly" :value="cellData.value" :placeholder="cellData.placeholder" class="w-full" />
                      <slot v-else-if="cellData.type === 'calcul'" name="calcul" v-bind:data="tr" v-bind:data_i="indextr"></slot>
                      <ml-input v-else-if="cellData.type === 'fixed-text'" :readonly="true" v-model="tr[cellData.id]" :placeholder="cellData.placeholder" class="w-full" />
                      <number-input v-else-if="cellData.type === 'number'" :readonly="board.readonly" v-model="tr[cellData.id]" :placeholder="cellData.placeholder" class="w-full" />
                      <ml-textarea
                        v-else-if="cellData.type === 'textarea'"
                        height="90px"
                        :readonly="board.readonly"
                        v-model="tr[cellData.id]"
                        :placeholder="typeof cellData.placeholder === 'string' ? cellData.placeholder : cellData.placeholder[indextr]"
                        class="w-full resize-none"
                      />
                      <ml-input v-else-if="cellData.type === 'select' && board.readonly" v-model="tr[cellData.id]" readonly class="w-full" />
                      <ml-input v-else-if="cellData.type === 'select' && tr[cellData.id] === '직접입력'" @blur="(e, data) => customSelectBlur(indextr, cellData.id, data)" class="w-full" :renderFocus="true" />
                      <vs-select v-else-if="cellData.type === 'select'" v-model="tr[cellData.id]" :placeholder="cellData.placeholder" class="w-full" autocomplete>
                        <vs-select-item v-for="selectData in cellData.selectData" :key="selectData" :text="selectData" :value="selectData" />
                      </vs-select>
                      <date-selector v-else-if="cellData.type === 'date'" class="w-full" v-model="tr[cellData.id]" :readonly="board.readonly" />
                      <time-selector v-else-if="cellData.type === 'time'" class="w-full" v-model="tr[cellData.id]" :readonly="board.readonly" />
                      <period-picker v-else-if="cellData.type === 'period'" v-model="tr[cellData.id]" :readonly="board.readonly" />
                      <CompanySelector v-else-if="cellData.type === 'company'" v-model="tr[cellData.id]" :readonly="board.readonly" />
                      <field-selector v-else-if="cellData.type === 'field'" v-model="tr[cellData.id]" :readonly="board.readonly" />
                      <div v-else-if="cellData.type === 'dateArr' && !!tr[cellData.id]" class="mobile-date-range">
                        <date-selector v-model="tr[cellData.id].sDate" placeholder="시작일" :readonly="board.readonly" class="date-input"></date-selector>
                        <span class="date-separator">~</span>
                        <date-selector v-model="tr[cellData.id].eDate" placeholder="종료일" :readonly="board.readonly" class="date-input"></date-selector>
                      </div>
                      <sign-field v-else-if="cellData.type === 'sign'" v-model="tr[cellData.id]" :readonly="board.readonly" />
                      <div v-else-if="cellData.type === 'radio'" class="mobile-radio-group">
                        <vs-radio v-for="(radioItem, radioItem_i) in cellData.radioItems" :key="`mobile_${indextr}_${radioItem_i}`" v-model="tr[cellData.id]" :vs-name="`mobile_${indextr}_${radioItem_i}`" :vs-value="radioItem.value" class="mobile-radio-item">
                          {{ radioItem.label || radioItem.value }}
                        </vs-radio>
                      </div>
                      <div v-else-if="cellData.type === 'check'" class="mobile-check-single">
                        <vs-checkbox :value="tr[cellData.id] === cellData.title" @input="tr[cellData.id] = tr[cellData.id] === cellData.title ? '' : cellData.title" />
                        <span class="check-label">{{ cellData.title }}</span>
                      </div>
                      <div v-else-if="cellData.type === 'customType'">
                        <slot name="customType" :index="indextr" :cellData="cellData" :trData="tr" :value="tr[cellData.id]" :input="(data) => customTypeInput(indextr, cellData.id, data)"></slot>
                      </div>
                    </template>
                  </template>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 모바일 하단 항목 추가 버튼 (아이템이 있을 때만) -->
      <div class="mobile-add-button-container" v-if="!isPrint && list.length > 0 && controls && !board.readonly">
        <vs-button
          class="mobile-add-btn"
          color="primary"
          type="filled"
          size="large"
          @click="handleAddRow"
        >
          <i class="feather icon-plus mr-2"></i>
          항목 추가
        </vs-button>
      </div>
    </div>

    <!-- 삭제 확인 팝업 -->
    <vs-popup class="popup-example" title="삭제 확인" :active.sync="showDeleteConfirm">
      <div class="popup-content">
        <p class="delete-message">{{ deleteMessage }}</p>
        <div class="popup-buttons">
          <vs-button
            color="danger"
            type="filled"
            @click="confirmDelete"
          >
            삭제
          </vs-button>
          <vs-button
            color="dark"
            type="flat"
            @click="cancelDelete"
          >
            취소
          </vs-button>
        </div>
      </div>
    </vs-popup>

    <vs-popup id="popupRiskInfo" :title="popupHead" :active.sync="showInfoPopup">
      <div style="padding: 16px">
        <span v-html="popupBody" />
      </div>
    </vs-popup>
  </div>
</template>

<script>
import DateSelector from "./selector/DateSelector";
import TimeSelector from "./selector/TimeSelector";
import PeriodPicker from "./PeriodPicker";
import CompanySelector from "@/components/selector/CompanySelector";
import SignField from "@/components/SignField";
import FieldSelector from "@/components/selector/FieldSelector";
import NumberInput from "@/components/NumberInput.vue";

export default {
  name: "TableCustom",
  components: {
    NumberInput,
    SignField,
    PeriodPicker,
    TimeSelector,
    DateSelector,
    CompanySelector,
    FieldSelector,
  },
  props: {
    isPrint: Boolean,
    board: Object,
    controls: {
      type: Boolean,
      default: true,
    },
    multiple: {
      type: Boolean,
      default: true,
    },
    thema: {
      type: String,
      default: "default",
    },
    title: String,
    subtitle: String,
    titleType: String,
    // 팝업관련
    tooltipHead: String,
    tooltipBody: String,
    popupHead: String,
    popupBody: String,
    tableData: Array,
    value: Array,
    maxSize: {
      type: Number,
      default: 0,
    },
    mobile: {
      type: Array,
      default: () => [],
    },
    // 직접 추가 버튼을 제어할 경우 TRUE
    customAddHandler: {
      type: Boolean,
      default: false,
    },
    // list 최소 개수
    defaultSize: {
      type: Number,
      default: 1,
    }
  },
  computed: {
    isMobile () {
      return !(this.$store.state.verticalNavMenuWidth === 'default')
    },
    selectedItemsCount () {
      return this.checkData.length
    },
  },
  data() {
    return {
      checkData: [],
      list: this.value,
      dateArrCheck: [],
      numberId: 0 < this.value.length ? this.value[this.value.length - 1].id : 0,
      showInfoPopup: false,
      showDeleteConfirm: false,
      deleteMessage: '',
      deleteAction: null,
      deleteIndex: null,
    };
  },
  watch: {
    value: {
      handler(value) {
        this.list = value;
        this.numberId = 0 < value.length ? value[value.length - 1].id : 0;
      },
    },
    list: {
      immediate: true,
      deep: true,
      handler(value) {
        // 만약 value 개수가 없다면, 새로운 데이터를 추가한다.
        for (let i = value.length; i < this.defaultSize; i++) {
          this.handleAddRow();
        }

        this.$emit("input", value);
      },
    },
  },
  created() {
    // 추가버튼때 데이터를 넣기위해 dateArr의 id 값을 찾는다.
    this.dateArrCheck = this.tableData.map((td) => {
      if (td.type === "dateArr") return td.id;
    });
  },
  methods: {

    getItemBodyClass(cellData) {
      let classNames = "";
      if (cellData.type === 'no' || cellData.type === 'label' || cellData.type === 'string' || cellData.middle) {
        classNames += 'align-center'

        if (this.isPrint) {
          classNames += ' table-cell'
        } else {
          classNames += ' hidden xl:table-cell'
        }

      } else {
        classNames += 'align-top'

        if (this.isPrint) {
          classNames += ' table-cell'
        } else {
          classNames += ' hidden xl:table-cell'
        }
      }
      return classNames;
    },

    // All 체크박스 클릭
    onClickAllCheckbox() {
      if (this.checkData.length === this.list.length) this.checkData = [];
      else this.checkData = this.list.map((item) => item.id);
    },
    // 항목추가
    handleAddRow() {
      if (this.customAddHandler) {
        this.$emit("add-row", ++this.numberId);
        return;
      }

      if (this.maxSize > 0) {
        if (this.list.length >= this.maxSize) {
          alert(`${this.maxSize}개 이하로 입력이 가능합니다.`);
          return;
        }
      }

      this.numberId++
      const randomId = Math.floor(Math.random() * 1000000) + new Date().getTime();
      const addData = { id: randomId };
      if (0 < this.dateArrCheck.length) {
        this.dateArrCheck.map((_adc) => {
          addData[_adc] = {};
        });
      }
      this.list.push(addData);
    },
    // 선택삭제 확인
    handleRemoveRowConfirm() {
      const selectedCount = this.checkData.length
      if (selectedCount === 0) {
        this.$vs.notify({
          title: '알림',
          text: '삭제할 항목을 선택해주세요.',
          color: 'warning'
        })
        return
      }

      this.deleteMessage = `선택된 ${selectedCount}개의 항목을 삭제하시겠습니까?`
      this.deleteAction = 'batch'
      this.showDeleteConfirm = true
    },
    // 선택삭제 실행
    handleRemoveRow() {
      this.list = this.list.filter((item) => !this.checkData.includes(item.id));

      // 만약 list 개수가 없다면, 새로운 데이터를 추가한다.
      for (let i = this.list.length; i < this.defaultSize; i++) {
        this.handleAddRow();
      }

      this.checkData = [];

      this.$vs.notify({
        title: '삭제 완료',
        text: '선택된 항목이 삭제되었습니다.',
        color: 'success'
      })
    },
    // 개별 항목 삭제 확인
    handleDeleteSingleItem(index) {
      this.deleteMessage = `${index + 1}번째 항목을 삭제하시겠습니까?`
      this.deleteAction = 'single'
      this.deleteIndex = index
      this.showDeleteConfirm = true
    },
    // 개별 항목 삭제 실행
    handleRemoveSingleRow() {
      this.list.splice(this.deleteIndex, 1)

      // 만약 list 개수가 없다면, 새로운 데이터를 추가한다.
      for (let i = this.list.length; i < this.defaultSize; i++) {
        this.handleAddRow();
      }

      this.$vs.notify({
        title: '삭제 완료',
        text: '항목이 삭제되었습니다.',
        color: 'success'
      })
    },
    // 삭제 확인
    confirmDelete() {
      if (this.deleteAction === 'batch') {
        this.handleRemoveRow()
      } else if (this.deleteAction === 'single') {
        this.handleRemoveSingleRow()
      }
      this.showDeleteConfirm = false
    },
    // 삭제 취소
    cancelDelete() {
      this.showDeleteConfirm = false
      this.deleteAction = null
      this.deleteIndex = null
      this.deleteMessage = ''
    },
    // 전체 선택 토글 (모바일 헤더 클릭 시)
    toggleAllCheck() {
      if (this.checkData.length === this.list.length) {
        this.checkData = []
      } else {
        this.checkData = this.list.map((item) => item.id)
      }
    },


    customTypeInput(index, key, data) {
      this.$set(this.list[index], key, data);
    },

    // Select 직접입력
    customSelectBlur(index, key, data) {
      this.$set(this.list[index], key, "");
      this.$nextTick(() => {
        this.$set(this.list[index], key, data);
      });
    },

    // tooltip
    onClickToolTip() {
      this.showInfoPopup = true;
    },
    /**
     * 체크박스를 클릭했을 때 호출되는 메서드입니다.
     *
     * @param data 체크박스의 데이터
     * @param tr 행 데이터
     * @param id 식별자
     */
    onClickCheckbox(data, tr, id) {
      if (tr[id] && tr[id] === data) {
        this.$set(tr, id, "");
      } else {
        this.$set(tr, id, data);
      }
    }
  },
};
</script>
<style scoped lang="scss">
$border: 1px solid #cdcdcd;
$bg: #f9f9f9;
$mobile-breakpoint: 1280px;
$mobile-card-bg: #ffffff;
$mobile-card-border: 1px solid #e8e8e8;
$mobile-card-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
$mobile-selected-bg: #f0f9ff;
$mobile-selected-border: 1px solid #3b82f6;

.label-text {
  width: 114px;
  font-weight: 500;
  font-size: 14px;
}

.header-flex {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-buttons {
  display: flex;
  align-items: center;
}

.desktop-only {
  display: inline-flex;
}

/* 반응형 레이아웃 제어 */
.desktop-layout {
  display: block;
}
.mobile-layout {
  display: none;
}

/* 빈 목록 안내 메시지 */
.empty-list-message {
  padding: 60px 20px;
  text-align: center;
  background: #f8fafc;
  border: 2px dashed #cbd5e1;
  border-radius: 12px;
  margin-bottom: 20px;
}

.empty-content {
  max-width: 300px;
  margin: 0 auto;
}

.empty-icon {
  font-size: 48px;
  color: #94a3b8;
  margin-bottom: 16px;
}

.empty-text {
  font-size: 16px;
  color: #64748b;
  margin-bottom: 24px;
  font-weight: 500;
}

.empty-add-btn {
  min-height: 44px;
  padding: 12px 24px;
  font-weight: 600;

  .feather {
    font-size: 16px;
  }
}

table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;

  thead {
    background: #f9f9f9;
    z-index: 0 !important;

    th {
      font-style: normal;
      font-weight: 500;
      font-size: 14px;
      line-height: 18px;
      color: #3c3c3c;

      border-top: solid 1px #cdcdcd;
      border-bottom: solid 1px #cdcdcd;
      padding: 16px 0;
    }
  }
  tbody {
    tr {
      background: #fff !important;
      border-bottom: solid 1px #cdcdcd;

      td {
        padding: 10px 6px;
      }
    }
  }
}

table.label-table {
  @media (min-width: theme("screens.xl")) {
    &,
    tr,
    th,
    td {
      border: 1px solid #cdcdcd;
    }
    td {
      padding: 4px !important;
    }

    thead {
      th {
        background: #f9f9f9;
      }
    }
  }
}
.signatureImg {
  border: 1px solid #00000033;
  border-radius: 6px;
  min-height: 80px;
  max-width: 120px;
}

div::v-deep .input-span-placeholder {
  text-align: left;
}

div::v-deep .input-select::placeholder,
div::v-deep textarea::placeholder {
  font-size: 0.85rem;
  color: rgba(0, 0, 0, 0.4);
  font-weight: 300;
}

@media (max-width: $mobile-breakpoint) {
  .desktop-layout {
    display: none;
  }
  .mobile-layout {
    display: block;
  }

  /* 모바일에서 항목추가 버튼 숨김 */
  .desktop-only {
    display: none !important;
  }

  /* 모바일 헤더 최적화 */
  .header-flex {
    .header-buttons {
      .vs-button {
        min-height: 40px;
        font-size: 14px;
        padding: 8px 16px;
      }
    }
  }

  /* 빈 목록 메시지 모바일 최적화 */
  .empty-list-message {
    padding: 40px 16px;
    margin-bottom: 16px;
  }

  .empty-icon {
    font-size: 40px;
  }

  .empty-text {
    font-size: 15px;
  }
}

/* Mobile Styles */
.mobile-header {
  display: flex;
  align-items: center;
  padding: 15px;
  background-color: $bg;
  border: $border;
  border-radius: 8px;
  margin-bottom: 15px;
  gap: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;

  &:hover {
    background-color: #f1f5f9;
    border-color: #94a3b8;
  }

  &:active {
    transform: scale(0.98);
  }

  .mobile-header-text {
    font-weight: 500;
    font-size: 16px;
    color: #374151;
    flex: 1;
  }

  .vs-checkbox {
    transform: scale(1.2);
    pointer-events: none;
  }
}

.mobile-cards-container {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.mobile-card {
  background: $mobile-card-bg;
  border: $mobile-card-border;
  border-radius: 12px;
  box-shadow: $mobile-card-shadow;
  overflow: hidden;
  transition: all 0.3s ease;

  &.selected {
    background: $mobile-selected-bg;
    border: $mobile-selected-border;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
  }

  &.print-mode {
    box-shadow: none;
    border: $border;
    margin-bottom: 10px;
  }
}

.mobile-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 15px 20px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-bottom: 1px solid #e2e8f0;

  &.print-header {
    background: $bg;
    justify-content: center;
  }

  .mobile-card-header-left {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .card-index {
    font-weight: 600;
    font-size: 16px;
    color: #1e293b;
  }

  .vs-checkbox {
    transform: scale(1.2);
  }

  .mobile-delete-btn {
    min-height: 32px;
    padding: 8px 12px;
    font-size: 12px;
    font-weight: 500;
    border-radius: 6px;

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 2px 6px rgba(220, 38, 38, 0.3);
    }
  }
}

.mobile-card-content {
  padding: 20px;
}

.mobile-field {
  margin-top: 10px;
  margin-bottom: 14px;

  &:last-child {
    margin-bottom: 0;
  }
}

.mobile-field-label {
  display: block;
  font-weight: 600;
  font-size: 14px;
  color: #374151;
  margin-bottom: 8px;
  line-height: 1.4;
}

.mobile-field-input {
  .vs-select,
  .vs-input,
  .vs-textarea {
    min-height: 44px;

    .vs-select__input,
    .vs-input__input,
    .vs-textarea__input {
      font-size: 16px;
      padding: 12px 16px;
    }
  }

  .vs-select {
    .vs-select__input {
      border-radius: 8px;
      border: 1px solid #d1d5db;

      &:focus {
        border-color: #3b82f6;
        box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
      }
    }
  }

  .vs-textarea {
    .vs-textarea__input {
      border-radius: 8px;
      border: 1px solid #d1d5db;
      min-height: 80px;
      resize: vertical;

      &:focus {
        border-color: #3b82f6;
        box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
      }
    }
  }
}

.mobile-print-value {
  display: block;
  padding: 12px 16px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  color: #374151;
  min-height: 20px;

  &.mobile-print-textarea {
    min-height: 60px;
    white-space: pre-wrap;
    line-height: 1.5;
  }
}

.mobile-checks-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.mobile-check-item {
  display: flex;
  align-items: center;
  gap: 8px;

  .check-label {
    font-size: 14px;
    color: #374151;
  }
}

.mobile-check-single {
  display: flex;
  align-items: center;
  gap: 8px;

  .check-label {
    font-size: 14px;
    color: #374151;
  }
}

.mobile-radio-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.mobile-radio-item {
  font-size: 14px;
  color: #374151;
}

// 라디오 버튼 필드에 추가 하단 간격 적용
.mobile-radio-group {
  margin-bottom: 16px;
}

// 라디오 버튼을 포함한 mobile-field 전체에 추가 간격
.mobile-field {
  &.radio-field {
    margin-bottom: 32px;
  }

  &.custom-type-field {
    margin-top: 8px;
  }
}

.mobile-date-range {
  display: flex;
  flex-direction: column;
  gap: 8px;

  .date-input {
    flex: 1;
  }

  .date-separator {
    text-align: center;
    color: #6b7280;
    font-weight: 500;
  }
}

.mobile-print-sign {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 12px;
}

.mobile-signature-img {
  border: 1px solid #d1d5db;
  border-radius: 6px;
  max-width: 100px;
  max-height: 60px;
}

/* 모바일 하단 항목 추가 버튼 */
.mobile-add-button-container {
  margin-top: 20px;
  padding: 0 5px;
}

.mobile-add-btn {
  width: 100%;
  min-height: 50px;
  font-size: 16px;
  font-weight: 600;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  .feather {
    font-size: 18px;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(59, 130, 246, 0.3);
  }
}

/* 삭제 확인 팝업 스타일 */
.popup-content {
  padding: 20px;
  text-align: center;
}

/* PC subtitle 스타일 */
.subtitle-pc {
  margin-left: 12px;
  font-size: 14px;
  color: #6b7280;
  font-weight: 400;
  font-style: italic;
}

/* Mobile subtitle 스타일 */
.mobile-subtitle {
  margin-bottom: 15px;
  padding: 8px 8px;
  background: #f8fafc;
  border-radius: 4px;
  border-left: 2px solid #3b82f6;

  span {
    font-size: 12px;
    color: #475569;
    font-weight: 400;
    line-height: 1.4;
  }
}

/* 반응형 처리 */
@media (min-width: $mobile-breakpoint) {
  .mobile-subtitle {
    display: none;
  }
}

@media (max-width: $mobile-breakpoint) {
  .subtitle-pc {
    display: none;
  }
}

.delete-message {
  font-size: 16px;
  color: #374151;
  margin-bottom: 25px;
  line-height: 1.5;
}

.popup-buttons {
  display: flex;
  gap: 15px;
  justify-content: center;

  .vs-button {
    min-width: 100px;
    min-height: 40px;
    font-weight: 600;
  }
}

/* 작은 모바일 화면 최적화 */
@media (max-width: 480px) {
  .mobile-card-content {
    padding: 15px;
  }

  .mobile-header {
    padding: 12px;
  }

  .mobile-card-header {
    padding: 12px 15px;

    .mobile-card-header-left {
      .card-index {
        font-size: 14px;
      }
    }

    .mobile-delete-btn {
      min-height: 28px;
      padding: 6px 10px;
      font-size: 11px;
    }
  }

  .mobile-add-btn {
    min-height: 48px;
    font-size: 15px;

    .feather {
      font-size: 16px;
    }
  }

  .popup-buttons {
    flex-direction: column;
    gap: 10px;

    .vs-button {
      width: 100%;
    }
  }
}

/* 인쇄 시 PC 레이아웃 강제 적용 */
@media print {
  .desktop-layout {
    display: block !important;
  }
  .mobile-layout {
    display: none !important;
  }

  /* 테이블 인쇄 최적화 */
  .desktop-table-header,
  .desktop-table-row {
    break-inside: avoid;
    page-break-inside: avoid;
  }
}
</style>
